import { round } from "lodash";
import moment from "moment";

import { type CurrencyCode, formatting } from "@north-beam/nb-common";

export const DATE_FORMAT = "YYYY-MM-DD";
const roundToOneSigFig = (num: number) => Math.round(num * 10) / 10;
export const formatPercentage = (num: number) => {
  const rounded = roundToOneSigFig(num);
  if (rounded === Math.round(num)) {
    return `${rounded}.0%`;
  }
  return `${rounded}%`;
};

export const formatDate = (date: string) => {
  return moment(date, DATE_FORMAT).format("MMM DD, YYYY");
};

export const getIsoDate = (date: moment.Moment | Date) => {
  if (moment.isMoment(date)) {
    return date.format(DATE_FORMAT);
  } else {
    return date.toISOString().split("T")[0];
  }
};

export const todayIsoDate = () => {
  return getIsoDate(moment());
};

export const getDate = (date: string) => {
  return moment(date, DATE_FORMAT);
};

export const formatCurrencyToDollarAmount = (
  amount: number,
  currencyCode: CurrencyCode,
) => {
  return formatting
    .formatCurrency(amount, {
      precision: "exact",
      code: currencyCode,
    })
    .replace(/\.[0-9][0-9]/, "");
};

export const formatCurrencyExact = (
  amount: number,
  currencyCode: CurrencyCode,
) => {
  return formatting.formatCurrency(amount, {
    precision: "exact",
    code: currencyCode,
  });
};

export const formatCurrencyApprox = (
  amount: number,
  currencyCode: CurrencyCode,
) => {
  return formatting.formatCurrency(amount, {
    precision: "approximate",
    code: currencyCode,
  });
};

export const formatCurrencyApproxNoDecimal = (
  amount: number,
  currencyCode: CurrencyCode,
) => {
  return formatting
    .formatCurrency(amount, {
      precision: "approximate",
      code: currencyCode,
    })
    .replace(/\.[0-9]*/, "");
};

export const formatPotentiallyInfiniteNumber = (
  value: number,
  formatter: (val: number) => string,
  infFormatter: (val: string) => string = (val: string) => val,
) => {
  if (Number.isFinite(value)) {
    return formatter(value);
  }

  return infFormatter("∞");
};

export const roundPotentiallyInfiniteNumber = (
  value: number,
  decimalPlaces = 2,
) => {
  return formatPotentiallyInfiniteNumber(value, (num) =>
    String(round(num, decimalPlaces)),
  );
};

export const getCurrencySymbol = (code: CurrencyCode) =>
  (0)
    .toLocaleString("EN-us", {
      style: "currency",
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/\d/g, "");
